import type { ReactNode } from "react";
import { PrivyProvider as BasePrivyProvider } from "@privy-io/react-auth";
import { toSolanaWalletConnectors } from "@privy-io/react-auth/solana";
import { createSolanaRpc, createSolanaRpcSubscriptions } from "@solana/kit";
import { SOLANA_RPC_URL } from "../contracts/solana/config";

const APP_ID = import.meta.env.VITE_PRIVY_APP_ID as string | undefined;
// wss endpoint derived from our RPC (https→wss) for Privy's tx-confirm subscriptions.
const SOLANA_WS_URL = SOLANA_RPC_URL.replace(/^http/i, "ws");

// External Solana wallet connectors (Phantom / Solflare / Backpack …) for the
// "wallet" login method. Email/Google logins get a Privy embedded SOLANA wallet
// instead (see embeddedWallets below).
const solanaConnectors = toSolanaWalletConnectors();

export function PrivyProvider({ children }: { children: ReactNode }) {
  if (!APP_ID) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white p-8 text-center">
        <div className="max-w-md">
          <h1 className="text-xl font-bold mb-3">Privy not configured</h1>
          <p className="text-sm text-white/70">
            Set <code className="text-[#FF6B00]">VITE_PRIVY_APP_ID</code> in{" "}
            <code className="text-[#FF6B00]">apps/web/.env</code>. Get one at{" "}
            <a href="https://dashboard.privy.io" className="underline text-[#FF6B00]" target="_blank" rel="noreferrer">
              dashboard.privy.io
            </a>
            .
          </p>
        </div>
      </div>
    );
  }

  return (
    <BasePrivyProvider
      appId={APP_ID}
      config={{
        appearance: {
          theme: "dark",
          accentColor: "#FF6B00",
          logo: undefined,
          showWalletLoginFirst: false,
          // Show Solana wallets in the connect modal (not EVM).
          walletChainType: "solana-only",
        },
        loginMethods: ["email", "google", "wallet"],
        // Provision a SOLANA embedded wallet for users who log in without one
        // (email / google). The new Privy app (cmqwoav…) has Solana enabled.
        embeddedWallets: {
          solana: { createOnLogin: "users-without-wallets" },
        },
        externalWallets: {
          solana: { connectors: solanaConnectors },
        },
        // Pin Privy's Solana cluster to mainnet via OUR RPC (not Privy's public
        // default). NOTE: deposit/withdraw no longer send through Privy — they
        // sign via useSignTransaction and broadcast/confirm in-app (see
        // escrow.sendSignedTransaction); Privy's own confirm needs a
        // signatureSubscribe websocket the prod RPC lacks, which threw AFTER
        // the tx landed. Shape per @privy-io/react-auth v3 (config.solana.rpcs).
        solana: {
          rpcs: {
            "solana:mainnet": {
              rpc: createSolanaRpc(SOLANA_RPC_URL),
              rpcSubscriptions: createSolanaRpcSubscriptions(SOLANA_WS_URL),
              blockExplorerUrl: "https://explorer.solana.com",
            },
          },
        },
      }}
    >
      {children}
    </BasePrivyProvider>
  );
}
